import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import UserLayout from '../components/UserLayout';
import { useAuth } from '../context/AuthContext';
import { useApplications } from '../context/ApplicationContext';
import { useWorkflow } from '../context/WorkflowContext';
import { useNotifications } from '../context/NotificationContext';
import Card, { CardHeader, CardBody } from '../components/Card';
import Button from '../components/Button';
import ReferenceNumberSearch from '../components/ReferenceNumberSearch';
import { FileTextIcon, CreditCardIcon, BuildingIcon, CalendarIcon, ChevronRightIcon, BellIcon, CheckCircleIcon, XCircleIcon, ClockIcon, AlertCircleIcon, SearchIcon, ArrowRightIcon, UserIcon, TagIcon, PlusIcon, LayersIcon, FileIcon, TrendingUpIcon, ActivityIcon, SparklesIcon } from 'lucide-react';
const Dashboard: React.FC = () => {
  const {
    user
  } = useAuth();
  const {
    getApplicationsByUserId,
    getPaymentsByUserId
  } = useApplications();
  const {
    getWorkflowInstanceByApplicationId
  } = useWorkflow();
  const {
    getNotificationsByUserId
  } = useNotifications();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [applicationWorkflows, setApplicationWorkflows] = useState<Record<string, any>>({});
  const [activeTab, setActiveTab] = useState<'overview' | 'applications' | 'payments'>('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const loadDashboardData = () => {
      try {
        setIsLoading(true);
        setError(null);
        if (!user) {
          setIsLoading(false);
          return;
        }
        try {
          const userApplications = getApplicationsByUserId(user.id) || [];
          setApplications(userApplications);
        } catch (err) {
          console.error('Error loading applications:', err);
          setApplications([]);
        }
        try {
          const userPayments = getPaymentsByUserId(user.id) || [];
          setPayments(userPayments);
        } catch (err) {
          console.error('Error loading payments:', err);
          setPayments([]);
        }
        try {
          const userNotifications = getNotificationsByUserId(user.id) || [];
          setNotifications(userNotifications);
        } catch (err) {
          console.error('Error loading notifications:', err);
          setNotifications([]);
        }
        const workflows: Record<string, any> = {};
        if (applications.length > 0) {
          applications.forEach(app => {
            try {
              const workflow = getWorkflowInstanceByApplicationId(app.id);
              if (workflow) {
                workflows[app.id] = workflow;
              }
            } catch (err) {
              console.error(`Error loading workflow for application ${app.id}:`, err);
            }
          });
        }
        setApplicationWorkflows(workflows);
      } catch (err) {
        console.error('Dashboard data loading error:', err);
        setError('Failed to load dashboard data. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };
    loadDashboardData();
  }, [user, getApplicationsByUserId, getPaymentsByUserId, getNotificationsByUserId, getWorkflowInstanceByApplicationId]);
  if (error) {
    return <UserLayout data-id="element-5086">
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8" data-id="element-5087">
          <div className="max-w-7xl mx-auto" data-id="element-5088">
            <div className="bg-white p-8 rounded-2xl shadow-xl text-center border border-red-100" data-id="element-5089">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-5090">
                <AlertCircleIcon className="h-8 w-8 text-red-500" data-id="element-5091" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5092">
                Error Loading Dashboard
              </h2>
              <p className="text-gray-600 mb-6" data-id="element-5093">{error}</p>
              <Button variant="primary" onClick={() => window.location.reload()} data-id="element-5094">
                Refresh Page
              </Button>
            </div>
          </div>
        </div>
      </UserLayout>;
  }
  if (isLoading) {
    return <UserLayout data-id="element-5095">
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8" data-id="element-5096">
          <div className="max-w-7xl mx-auto" data-id="element-5097">
            <div className="bg-white p-8 rounded-2xl shadow-xl text-center" data-id="element-5098">
              <div className="relative w-16 h-16 mx-auto mb-4" data-id="element-5099">
                <div className="absolute inset-0 border-4 border-green-200 rounded-full" data-id="element-5100"></div>
                <div className="absolute inset-0 border-4 border-green-600 rounded-full border-t-transparent animate-spin" data-id="element-5101"></div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5102">
                Loading Dashboard
              </h2>
              <p className="text-gray-600" data-id="element-5103">
                Please wait while we load your dashboard data...
              </p>
            </div>
          </div>
        </div>
      </UserLayout>;
  }
  const getStatusBadge = (status: string) => {
    const badges = {
      approved: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        icon: CheckCircleIcon,
        label: 'Approved'
      },
      rejected: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        icon: XCircleIcon,
        label: 'Rejected'
      },
      pending: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        icon: ClockIcon,
        label: 'Pending'
      },
      processing: {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        icon: ActivityIcon,
        label: 'Processing'
      },
      under_review: {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        icon: ActivityIcon,
        label: 'Under Review'
      },
      awaiting_payment: {
        bg: 'bg-purple-100',
        text: 'text-purple-800',
        icon: CreditCardIcon,
        label: 'Awaiting Payment'
      },
      awaiting_documents: {
        bg: 'bg-indigo-100',
        text: 'text-indigo-800',
        icon: FileTextIcon,
        label: 'Awaiting Documents'
      }
    };
    const badge = badges[status as keyof typeof badges] || {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      icon: AlertCircleIcon,
      label: status.replace('_', ' ').toUpperCase()
    };
    const Icon = badge.icon;
    return <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`} data-id="element-5104">
        <Icon className="w-3 h-3 mr-1.5" data-id="element-5105" />
        {badge.label}
      </span>;
  };
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (err) {
      return 'Invalid date';
    }
  };
  const getApplicationsStats = () => {
    const total = applications.length;
    const pending = applications.filter(app => ['pending', 'processing', 'under_review'].includes(app.status)).length;
    const completed = applications.filter(app => app.status === 'approved').length;
    const awaitingPayment = applications.filter(app => app.status === 'awaiting_payment').length;
    return {
      total,
      pending,
      completed,
      awaitingPayment
    };
  };
  const getPaymentsStats = () => {
    const total = payments.length;
    const amount = payments.reduce((sum, payment) => sum + payment.amount, 0);
    return {
      total,
      amount
    };
  };
  const stats = getApplicationsStats();
  const paymentStats = getPaymentsStats();
  const recentApplications = applications.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).slice(0, 5);
  const recentPayments = payments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 5);
  return <UserLayout data-id="element-5106">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50" data-id="element-5107">
        <div className="py-8 px-4 sm:px-6 lg:px-8" data-id="element-5108">
          <div className="max-w-7xl mx-auto" data-id="element-5109">
            {/* Welcome Header */}
            <div className="mb-8" data-id="element-5110">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-5111">
                <div className="mb-4 md:mb-0" data-id="element-5112">
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2" data-id="element-5113">
                    Welcome back,{' '}
                    <span className="text-green-600" data-id="element-5114">
                      {user?.name?.split(' ')[0] || 'User'}
                    </span>
                  </h1>
                  <p className="text-gray-600 text-lg" data-id="element-5115">
                    Here's what's happening with your services today
                  </p>
                </div>
                <Button variant="primary" onClick={() => navigate('/mdas')} className="flex items-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" data-id="element-5116">
                  <PlusIcon className="h-5 w-5 mr-2" data-id="element-5117" />
                  New Application
                </Button>
              </div>
            </div>

            {/* Quick Search Card */}
            <div className="mb-8" data-id="element-5118">
              <Card className="border border-green-100 shadow-lg hover:shadow-xl transition-shadow duration-300" data-id="element-5119">
                <CardBody className="p-6" data-id="element-5120">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-5121">
                    <div className="flex items-center" data-id="element-5122">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mr-4 shadow-lg" data-id="element-5123">
                        <SearchIcon className="h-6 w-6 text-white" data-id="element-5124" />
                      </div>
                      <div data-id="element-5125">
                        <h2 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-5126">
                          Track Your Application
                        </h2>
                        <p className="text-sm text-gray-600" data-id="element-5127">
                          Enter your reference number to check application
                          status
                        </p>
                      </div>
                    </div>
                    <div className="flex-grow md:max-w-md" data-id="element-5128">
                      <ReferenceNumberSearch data-id="element-5129" />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-5130">
              {[{
              icon: FileTextIcon,
              label: 'Total Applications',
              value: stats.total,
              color: 'from-green-500 to-green-600',
              bgColor: 'bg-green-50',
              link: '/my-applications',
              linkText: 'View all'
            }, {
              icon: ClockIcon,
              label: 'Pending',
              value: stats.pending,
              color: 'from-yellow-500 to-yellow-600',
              bgColor: 'bg-yellow-50',
              link: '/my-applications',
              linkText: 'View pending'
            }, {
              icon: CreditCardIcon,
              label: 'Total Payments',
              value: `₦${paymentStats.amount.toLocaleString()}`,
              color: 'from-purple-500 to-purple-600',
              bgColor: 'bg-purple-50',
              link: '/payment-history',
              linkText: 'View history'
            }, {
              icon: BuildingIcon,
              label: 'Services',
              value: '100+',
              color: 'from-blue-500 to-blue-600',
              bgColor: 'bg-blue-50',
              link: '/mdas',
              linkText: 'Browse'
            }].map((stat, index) => <div key={index} className="group" style={{
              animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
            }} data-id="element-5131">
                  <Card className="h-full border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1" data-id="element-5132">
                    <CardBody className="p-6" data-id="element-5133">
                      <div className="flex items-start justify-between mb-4" data-id="element-5134">
                        <div className={`w-14 h-14 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center shadow-lg transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`} data-id="element-5135">
                          <stat.icon className="h-7 w-7 text-white" data-id="element-5136" />
                        </div>
                        <TrendingUpIcon className="h-5 w-5 text-green-500" data-id="element-5137" />
                      </div>
                      <p className="text-sm font-medium text-gray-600 mb-1" data-id="element-5138">
                        {stat.label}
                      </p>
                      <p className="text-3xl font-bold text-gray-900 mb-4" data-id="element-5139">
                        {stat.value}
                      </p>
                      <Link to={stat.link} className="text-sm font-medium text-green-600 hover:text-green-700 flex items-center group-hover:translate-x-1 transition-transform duration-300" data-id="element-5140">
                        {stat.linkText}
                        <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-5141" />
                      </Link>
                    </CardBody>
                  </Card>
                </div>)}
            </div>

            {/* Tabs */}
            <div className="mb-8" data-id="element-5142">
              <div className="bg-white rounded-2xl shadow-lg p-2 inline-flex" data-id="element-5143">
                {[{
                key: 'overview',
                label: 'Overview'
              }, {
                key: 'applications',
                label: 'Applications'
              }, {
                key: 'payments',
                label: 'Payments'
              }].map(tab => <button key={tab.key} onClick={() => setActiveTab(tab.key as typeof activeTab)} className={`px-6 py-3 rounded-xl font-medium text-sm transition-all duration-300 ${activeTab === tab.key ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-5144">
                    {tab.label}
                  </button>)}
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-5145">
                {/* Quick Actions */}
                <div className="lg:col-span-1" data-id="element-5146">
                  <Card className="border-0 shadow-xl" data-id="element-5147">
                    <CardHeader className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-b-0" data-id="element-5148">
                      <h2 className="text-xl font-bold text-gray-900 flex items-center" data-id="element-5149">
                        <SparklesIcon className="h-5 w-5 mr-2 text-green-600" data-id="element-5150" />
                        Quick Actions
                      </h2>
                    </CardHeader>
                    <CardBody className="p-4" data-id="element-5151">
                      <div className="space-y-3" data-id="element-5152">
                        {[{
                      icon: BuildingIcon,
                      title: 'Apply for Service',
                      desc: 'Browse available services',
                      link: '/mdas',
                      color: 'blue'
                    }, {
                      icon: SearchIcon,
                      title: 'Track Application',
                      desc: 'Check application status',
                      link: '/track-application',
                      color: 'green'
                    }, {
                      icon: FileTextIcon,
                      title: 'My Applications',
                      desc: 'View submitted applications',
                      link: '/my-applications',
                      color: 'yellow'
                    }, {
                      icon: CreditCardIcon,
                      title: 'Payment History',
                      desc: 'View payment records',
                      link: '/payment-history',
                      color: 'purple'
                    }, {
                      icon: UserIcon,
                      title: 'Update Profile',
                      desc: 'Manage account info',
                      link: '/profile',
                      color: 'indigo'
                    }, {
                      icon: AlertCircleIcon,
                      title: 'Get Support',
                      desc: 'Contact help desk',
                      link: '/support',
                      color: 'red'
                    }].map((action, index) => <Link key={index} to={action.link} className="group flex items-center p-4 bg-gradient-to-r from-white to-gray-50 border border-gray-100 rounded-xl hover:shadow-lg transition-all duration-300 hover:scale-[1.02]" data-id="element-5153">
                            <div className={`p-3 rounded-xl bg-${action.color}-100 text-${action.color}-600 mr-4 group-hover:scale-110 transition-transform duration-300`} data-id="element-5154">
                              <action.icon className="h-5 w-5" data-id="element-5155" />
                            </div>
                            <div className="flex-1" data-id="element-5156">
                              <p className="text-sm font-semibold text-gray-900 group-hover:text-green-600 transition-colors" data-id="element-5157">
                                {action.title}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-5158">
                                {action.desc}
                              </p>
                            </div>
                            <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-green-600 group-hover:translate-x-1 transition-all duration-300" data-id="element-5159" />
                          </Link>)}
                      </div>
                    </CardBody>
                  </Card>
                </div>

                {/* Recent Activity */}
                <div className="lg:col-span-2 space-y-8" data-id="element-5160">
                  {/* Recent Applications */}
                  <Card className="border-0 shadow-xl" data-id="element-5161">
                    <CardHeader className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 flex items-center justify-between border-b-0" data-id="element-5162">
                      <h2 className="text-xl font-bold text-gray-900" data-id="element-5163">
                        Recent Applications
                      </h2>
                      <Link to="/my-applications" className="text-sm font-medium text-green-600 hover:text-green-700 flex items-center" data-id="element-5164">
                        View all
                        <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-5165" />
                      </Link>
                    </CardHeader>
                    <CardBody className="p-0" data-id="element-5166">
                      {recentApplications.length === 0 ? <div className="p-8 text-center" data-id="element-5167">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-5168">
                            <FileTextIcon className="h-8 w-8 text-gray-400" data-id="element-5169" />
                          </div>
                          <p className="text-gray-500 mb-4" data-id="element-5170">
                            No applications found
                          </p>
                          <Button variant="outline" size="sm" onClick={() => navigate('/mdas')} data-id="element-5171">
                            Apply for a Service
                          </Button>
                        </div> : <div className="divide-y divide-gray-100" data-id="element-5172">
                          {recentApplications.map((application, index) => <div key={application.id} className="p-6 hover:bg-gradient-to-r hover:from-green-50 hover:to-transparent transition-all duration-300 cursor-pointer" onClick={() => navigate(`/track-application/${application.referenceNumber}`)} style={{
                      animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                    }} data-id="element-5173">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-5174">
                                <div className="flex-1" data-id="element-5175">
                                  <h3 className="text-base font-semibold text-gray-900 mb-2" data-id="element-5176">
                                    {application.serviceName}
                                  </h3>
                                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500" data-id="element-5177">
                                    <span className="flex items-center" data-id="element-5178">
                                      <TagIcon className="h-3 w-3 mr-1" data-id="element-5179" />
                                      {application.referenceNumber}
                                    </span>
                                    <span className="flex items-center" data-id="element-5180">
                                      <CalendarIcon className="h-3 w-3 mr-1" data-id="element-5181" />
                                      {formatDate(application.submittedAt)}
                                    </span>
                                    <span className="flex items-center" data-id="element-5182">
                                      <BuildingIcon className="h-3 w-3 mr-1" data-id="element-5183" />
                                      {application.mdaName}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex flex-col items-start sm:items-end gap-2" data-id="element-5184">
                                  {getStatusBadge(application.status)}
                                  {applicationWorkflows[application.id] && <div className="w-full sm:w-32" data-id="element-5185">
                                      <div className="flex items-center justify-between mb-1" data-id="element-5186">
                                        <span className="text-xs text-gray-500" data-id="element-5187">
                                          Progress
                                        </span>
                                        <span className="text-xs font-medium text-gray-700" data-id="element-5188">
                                          {Math.round((applicationWorkflows[application.id].currentStepIndex + 1) / applicationWorkflows[application.id].steps.length * 100)}
                                          %
                                        </span>
                                      </div>
                                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden" data-id="element-5189">
                                        <div className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all duration-500" style={{
                                width: `${(applicationWorkflows[application.id].currentStepIndex + 1) / applicationWorkflows[application.id].steps.length * 100}%`
                              }} data-id="element-5190"></div>
                                      </div>
                                    </div>}
                                </div>
                              </div>
                            </div>)}
                        </div>}
                    </CardBody>
                  </Card>

                  {/* Recent Notifications */}
                  <Card className="border-0 shadow-xl" data-id="element-5191">
                    <CardHeader className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 flex items-center justify-between border-b-0" data-id="element-5192">
                      <h2 className="text-xl font-bold text-gray-900" data-id="element-5193">
                        Recent Notifications
                      </h2>
                      <Link to="/notifications" className="text-sm font-medium text-green-600 hover:text-green-700 flex items-center" data-id="element-5194">
                        View all
                        <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-5195" />
                      </Link>
                    </CardHeader>
                    <CardBody className="p-0" data-id="element-5196">
                      {notifications.length === 0 ? <div className="p-8 text-center" data-id="element-5197">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-5198">
                            <BellIcon className="h-8 w-8 text-gray-400" data-id="element-5199" />
                          </div>
                          <p className="text-gray-500" data-id="element-5200">
                            No notifications found
                          </p>
                        </div> : <div className="divide-y divide-gray-100" data-id="element-5201">
                          {notifications.slice(0, 5).map((notification, index) => <div key={notification.id} className="p-6 hover:bg-gradient-to-r hover:from-blue-50 hover:to-transparent transition-all duration-300" style={{
                      animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                    }} data-id="element-5202">
                                <div className="flex items-start gap-4" data-id="element-5203">
                                  <div className="flex-shrink-0" data-id="element-5204">
                                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg" data-id="element-5205">
                                      <BellIcon className="h-5 w-5 text-white" data-id="element-5206" />
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-5207">
                                    <p className="text-sm font-semibold text-gray-900 mb-1" data-id="element-5208">
                                      {notification.title}
                                    </p>
                                    <p className="text-sm text-gray-600 mb-2" data-id="element-5209">
                                      {notification.message}
                                    </p>
                                    <p className="text-xs text-gray-400" data-id="element-5210">
                                      {formatDate(notification.createdAt)}
                                    </p>
                                  </div>
                                  {!notification.read && <div className="flex-shrink-0" data-id="element-5211">
                                      <span className="inline-block w-2 h-2 rounded-full bg-green-500" data-id="element-5212"></span>
                                    </div>}
                                </div>
                              </div>)}
                        </div>}
                    </CardBody>
                  </Card>
                </div>
              </div>}

            {activeTab === 'applications' && <Card className="border-0 shadow-xl" data-id="element-5213">
                <CardHeader className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-b-0" data-id="element-5214">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-5215">
                    All Applications
                  </h2>
                </CardHeader>
                <CardBody className="p-0" data-id="element-5216">
                  {applications.length === 0 ? <div className="p-12 text-center" data-id="element-5217">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-5218">
                        <FileTextIcon className="h-10 w-10 text-green-600" data-id="element-5219" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5220">
                        No applications yet
                      </h3>
                      <p className="text-gray-600 mb-6" data-id="element-5221">
                        Start by applying for a government service.
                      </p>
                      <Button variant="primary" onClick={() => navigate('/mdas')} data-id="element-5222">
                        Browse Services
                      </Button>
                    </div> : <div className="overflow-x-auto" data-id="element-5223">
                      <table className="min-w-full divide-y divide-gray-200" data-id="element-5224">
                        <thead className="bg-gray-50" data-id="element-5225">
                          <tr data-id="element-5226">
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-5227">
                              Service
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-5228">
                              Reference
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-5229">
                              Date
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-5230">
                              Status
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-5231">
                              Payment
                            </th>
                            <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-5232">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100" data-id="element-5233">
                          {applications.map(application => <tr key={application.id} className="hover:bg-gray-50 transition-colors" data-id="element-5234">
                              <td className="px-6 py-4" data-id="element-5235">
                                <div className="flex items-center" data-id="element-5236">
                                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center mr-3" data-id="element-5237">
                                    <FileIcon className="h-5 w-5 text-green-600" data-id="element-5238" />
                                  </div>
                                  <div data-id="element-5239">
                                    <div className="text-sm font-semibold text-gray-900" data-id="element-5240">
                                      {application.serviceName}
                                    </div>
                                    <div className="text-xs text-gray-500" data-id="element-5241">
                                      {application.mdaName}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900" data-id="element-5242">
                                {application.referenceNumber}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900" data-id="element-5243">
                                {formatDate(application.submittedAt)}
                              </td>
                              <td className="px-6 py-4" data-id="element-5244">
                                {getStatusBadge(application.status)}
                              </td>
                              <td className="px-6 py-4" data-id="element-5245">
                                {application.isPaid ? <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-5246">
                                    <CheckCircleIcon className="w-3 h-3 mr-1.5" data-id="element-5247" />
                                    Paid
                                  </span> : <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-5248">
                                    <AlertCircleIcon className="w-3 h-3 mr-1.5" data-id="element-5249" />
                                    Unpaid
                                  </span>}
                              </td>
                              <td className="px-6 py-4 text-right" data-id="element-5250">
                                <Button variant="outline" size="sm" onClick={() => navigate(`/track-application/${application.referenceNumber}`)} data-id="element-5251">
                                  View
                                </Button>
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>}
                </CardBody>
              </Card>}

            {activeTab === 'payments' && <Card className="border-0 shadow-xl" data-id="element-5252">
                <CardHeader className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-b-0" data-id="element-5253">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-5254">
                    Payment History
                  </h2>
                </CardHeader>
                <CardBody className="p-0" data-id="element-5255">
                  {payments.length === 0 ? <div className="p-12 text-center" data-id="element-5256">
                      <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-5257">
                        <CreditCardIcon className="h-10 w-10 text-purple-600" data-id="element-5258" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5259">
                        No payment records
                      </h3>
                      <p className="text-gray-600" data-id="element-5260">
                        Your payment history will appear here once you make
                        payments for services.
                      </p>
                    </div> : <div className="overflow-x-auto" data-id="element-5261">
                      <table className="min-w-full divide-y divide-gray-200" data-id="element-5262">
                        <thead className="bg-gray-50" data-id="element-5263">
                          <tr data-id="element-5264">
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-5265">
                              Service
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-5266">
                              Reference
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-5267">
                              Date
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-5268">
                              Amount
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-5269">
                              Status
                            </th>
                            <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-5270">
                              Receipt
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100" data-id="element-5271">
                          {payments.map(payment => <tr key={payment.id} className="hover:bg-gray-50 transition-colors" data-id="element-5272">
                              <td className="px-6 py-4" data-id="element-5273">
                                <div className="flex items-center" data-id="element-5274">
                                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mr-3" data-id="element-5275">
                                    <CreditCardIcon className="h-5 w-5 text-purple-600" data-id="element-5276" />
                                  </div>
                                  <div data-id="element-5277">
                                    <div className="text-sm font-semibold text-gray-900" data-id="element-5278">
                                      {payment.serviceName}
                                    </div>
                                    <div className="text-xs text-gray-500" data-id="element-5279">
                                      {payment.mdaName}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900" data-id="element-5280">
                                {payment.referenceNumber}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900" data-id="element-5281">
                                {formatDate(payment.date)}
                              </td>
                              <td className="px-6 py-4 text-sm font-semibold text-gray-900" data-id="element-5282">
                                ₦{payment.amount.toLocaleString()}
                              </td>
                              <td className="px-6 py-4" data-id="element-5283">
                                {payment.status === 'successful' ? <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-5284">
                                    <CheckCircleIcon className="w-3 h-3 mr-1.5" data-id="element-5285" />
                                    Successful
                                  </span> : payment.status === 'pending' ? <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-5286">
                                    <ClockIcon className="w-3 h-3 mr-1.5" data-id="element-5287" />
                                    Pending
                                  </span> : <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-5288">
                                    <XCircleIcon className="w-3 h-3 mr-1.5" data-id="element-5289" />
                                    Failed
                                  </span>}
                              </td>
                              <td className="px-6 py-4 text-right" data-id="element-5290">
                                {payment.status === 'successful' && <Button variant="outline" size="sm" onClick={() => navigate(`/payment-receipt/${payment.id}`)} data-id="element-5291">
                                    Receipt
                                  </Button>}
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>}
                </CardBody>
              </Card>}

            {/* Popular Services */}
            <div className="mt-12" data-id="element-5292">
              <div className="flex items-center justify-between mb-6" data-id="element-5293">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-5294">
                  Popular Government Services
                </h2>
                <Button variant="outline" onClick={() => navigate('/mdas')} data-id="element-5295">
                  View All Services
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5296">
                {[{
                icon: FileTextIcon,
                title: 'Tax Clearance Certificate',
                desc: 'Apply for your tax clearance certificate online',
                fee: 2500,
                id: '101',
                color: 'blue'
              }, {
                icon: LayersIcon,
                title: 'Certificate of Occupancy',
                desc: 'Apply for C of O for your land property',
                fee: 50000,
                id: '401',
                color: 'green'
              }, {
                icon: BuildingIcon,
                title: 'Business Permit Renewal',
                desc: 'Renew your business operation permit',
                fee: 5000,
                id: '102',
                color: 'purple'
              }].map((service, index) => <div key={index} className="group" style={{
                animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
              }} data-id="element-5297">
                    <Card className="h-full border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2" data-id="element-5298">
                      <CardBody className="p-6" data-id="element-5299">
                        <div className="flex items-center mb-4" data-id="element-5300">
                          <div className={`p-3 rounded-xl bg-${service.color}-100 text-${service.color}-600 mr-3 group-hover:scale-110 transition-transform duration-300`} data-id="element-5301">
                            <service.icon className="h-6 w-6" data-id="element-5302" />
                          </div>
                          <h3 className="text-lg font-bold text-gray-900 group-hover:text-green-600 transition-colors" data-id="element-5303">
                            {service.title}
                          </h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-4 leading-relaxed" data-id="element-5304">
                          {service.desc}
                        </p>
                        <div className="flex justify-between items-center pt-4 border-t border-gray-100" data-id="element-5305">
                          <span className="text-sm font-bold text-gray-900" data-id="element-5306">
                            Fee: ₦{service.fee.toLocaleString()}
                          </span>
                          <Button variant="outline" size="sm" onClick={() => navigate(`/services/${service.id}`)} data-id="element-5307">
                            Apply Now
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style data-id="element-5308">{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </UserLayout>;
};
export default Dashboard;